import { Suspense, lazy, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserRole } from '@lombok/shared';
import { Button, Card, CardContent } from '@lombok/ui';
import { useAuth } from '@/auth/AuthContext';
import { homePathForRole, ROLE_LABELS_ES } from '@/auth/roles';
import { Logo } from '@/components/Logo';
import { loginSchema, type LoginValues } from './loginSchema';

// Reuse the landing's living network so the auth flow feels like the same brand.
const NetworkField = lazy(() =>
  import('@/features/public-site/NetworkField').then((m) => ({ default: m.NetworkField })),
);

/** Demo roles surfaced ONLY in the dev fallback so every portal is reachable for review. */
const DEMO_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.FINANCE,
  UserRole.SALES_REP,
  UserRole.SALES_MANAGER,
  UserRole.CUSTOMER_USER,
  UserRole.DISTRIBUTOR_ADMIN,
  UserRole.WAREHOUSE,
];

/**
 * Login screen. Form state + validation via React Hook Form + Zod (schema from
 * @lombok/shared). On the real path this performs a Cognito sign-in (email +
 * password); the role comes from the verified JWT, so the role picker is hidden.
 * When no Cognito pool is configured ("modo desarrollo"), the picker is shown
 * so reviewers can land in any portal without a backend.
 */
export function LoginPage() {
  const { signIn, mode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const isDev = mode === 'dev';

  /** Top-level submit error (wrong credentials, new-password-required, etc.). */
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', role: isDev ? UserRole.ADMIN : undefined },
  });

  const onSubmit = async (values: LoginValues) => {
    setFormError(null);
    // Dev fallback selects the portal via the role picker; the dev provider
    // reads it from a `role:<value>` password token. Real auth sends the
    // actual password and ignores `role`.
    const password = isDev ? `role:${values.role ?? UserRole.ADMIN}` : values.password;
    try {
      const result = await signIn({ email: values.email, password });
      if (result.status === 'new_password_required') {
        setFormError(
          'Tu cuenta requiere establecer una nueva contraseña. Contacta al administrador para completar la activación.',
        );
        return;
      }
      const role = result.session.user.role;
      navigate(from ?? homePathForRole(role), { replace: true });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'No se pudo iniciar sesión. Intenta de nuevo.');
    }
  };

  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      {/* Brand panel (hidden on mobile to keep the form front-and-center). */}
      <div className="bg-brand-gradient hero-aurora relative hidden flex-col justify-between overflow-hidden p-10 text-brand-white lg:flex">
        <div className="pointer-events-none absolute inset-0 opacity-40">
          <Suspense fallback={null}>
            <NetworkField />
          </Suspense>
        </div>
        <Logo variant="light" className="relative h-8" />
        <motion.div
          className="relative max-w-sm"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <h2 className="text-2xl font-semibold leading-tight tracking-tight">
            Distribución <span className="text-brand-accent">premium</span> para las Américas
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-brand-silver">
            Lubricantes, fluidos industriales y soluciones comerciales. Operado con disciplina,
            entregado con confianza.
          </p>
        </motion.div>
        <p className="relative text-xs text-brand-silver">© Lombok Global Lubricants</p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <Logo className="h-8" />
          </div>
          <h1 className="text-xl font-semibold text-text-primary">Iniciar sesión</h1>
          <p className="mt-1 text-sm text-text-muted">
            Accede a tu portal de Lombok Commerce OS.
          </p>

          {isDev ? (
            <p
              className="mt-4 rounded-md border border-warning bg-warning-soft px-3 py-2 text-xs text-text-primary"
              role="note"
            >
              <span className="font-semibold">Modo desarrollo:</span> autenticación simulada (sin
              Cognito). Elige un portal para revisarlo. No válido en producción.
            </p>
          ) : null}

          <Card className="mt-6">
            <CardContent className="pt-5">
              <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)} noValidate>
                {formError ? (
                  <p
                    className="rounded-md border border-danger-soft bg-danger-soft px-3 py-2 text-sm font-medium text-danger"
                    role="alert"
                  >
                    {formError}
                  </p>
                ) : null}

                <Field label="Correo electrónico" htmlFor="email" error={errors.email?.message}>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="tucorreo@empresa.com"
                    className={inputClass}
                    {...register('email')}
                  />
                </Field>

                <Field label="Contraseña" htmlFor="password" error={errors.password?.message}>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className={inputClass}
                    {...register('password')}
                  />
                </Field>

                {isDev ? (
                  <Field
                    label="Portal (demo)"
                    htmlFor="role"
                    error={errors.role?.message}
                    hint="Solo en modo desarrollo. El rol real proviene del inicio de sesión seguro."
                  >
                    <select id="role" className={inputClass} {...register('role')}>
                      {DEMO_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {ROLE_LABELS_ES[role]}
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : null}

                <Button type="submit" block disabled={isSubmitting}>
                  {isSubmitting ? 'Ingresando…' : 'Entrar'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-sm text-text-muted">
            ¿No tienes cuenta?{' '}
            <Link to="/request-account" className="font-medium text-brand-primary hover:underline">
              Solicitar cuenta
            </Link>
          </p>
          <p className="mt-2 text-center text-sm">
            <Link to="/" className="text-text-muted hover:underline">
              ← Volver al inicio
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

const inputClass =
  'h-11 w-full rounded-md border border-border-strong bg-surface px-3 text-sm text-text-primary placeholder:text-text-muted focus-visible:border-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-text-primary">
        {label}
      </label>
      {children}
      {hint && !error ? <p className="text-xs text-text-muted">{hint}</p> : null}
      {error ? (
        <p className="text-xs font-medium text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
